import logger from "../../utils/logger";
import { supabase, getUserProfile, ensureValidSession } from './client';
import { canCreateInspection, canCreateInspectionForProperty } from './subscriptions';
import { uploadPhoto, getSignedPhotoUrl } from './storage';
import { canTenantAccessProperty } from './tenantInvitations';

/**
 * Helper to convert base64 string to File object
 * @param {string} base64String - Base64 encoded image string (with data:image/... prefix)
 * @param {string} fileName - File name for the created File object
 * @returns {File} File object
 */
function base64ToFile(base64String, fileName) {
  const arr = base64String.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], fileName, { type: mime });
}

/**
 * Get all inspections for the current user (across all properties)
 * @returns {Promise<Array>} Array of inspections
 */
export async function getAllInspections() {
  try {
    // Ensure session is valid before making request
    const isValid = await ensureValidSession();
    if (!isValid) {
      console.warn('[getAllInspections] Invalid session, returning empty array');
      return [];
    }

    const { data, error } = await supabase
      .from('inspections')
      .select(`
        *,
        leases (
          id,
          tenant_name,
          tenant_email,
          start_date,
          end_date,
          status
        ),
        inspection_rooms (
          *,
          inspection_items (
            *,
            inspection_photos (*)
          )
        )
      `)
      .order('date', { ascending: false });

    if (error) throw error;

    // Generate signed URLs for all photos in all inspections
    if (data) {
      logger.log(`🔍 [getAllInspections] Processing ${data.length} inspections`);

      for (const inspection of data) {
        if (inspection.inspection_rooms) {
          for (const room of inspection.inspection_rooms) {
            if (room.inspection_items) {
              for (const item of room.inspection_items) {
                if (item.inspection_photos && item.inspection_photos.length > 0) {
                  const photosWithUrls = await Promise.all(
                    item.inspection_photos.map(async (photo) => {
                      if (!photo.storage_path) {
                        return { ...photo, url: null };
                      }

                      const { url, error: urlError } = await getSignedPhotoUrl(photo.storage_path);

                      if (urlError) {
                        console.error(`[getAllInspections] Error getting signed URL for photo ${photo.id}:`, urlError);
                      }

                      return {
                        ...photo,
                        url: url || null,
                      };
                    })
                  );

                  item.inspection_photos = photosWithUrls;
                }
              }
            }
          }
        }
      }
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching all inspections:', error);
    throw error;
  }
}

/**
 * Get all inspections for a property
 * @param {string} propertyId - Property ID
 * @returns {Promise<Array>} Array of inspections
 */
export async function getInspections(propertyId) {
  try {
    // Ensure session is valid before making request
    const isValid = await ensureValidSession();
    if (!isValid) {
      console.warn('[getInspections] Invalid session, returning empty array');
      return [];
    }

    const { data, error } = await supabase
      .from('inspections')
      .select(`
        *,
        leases (
          id,
          tenant_name,
          tenant_email,
          start_date,
          end_date,
          status
        ),
        inspection_rooms (
          *,
          inspection_items (
            *,
            inspection_photos (*)
          )
        )
      `)
      .eq('property_id', propertyId)
      .order('date', { ascending: false });

    if (error) throw error;

    // Generate signed URLs for all photos in all inspections
    if (data) {
      for (const inspection of data) {
        if (inspection.inspection_rooms) {
          for (const room of inspection.inspection_rooms) {
            if (room.inspection_items) {
              for (const item of room.inspection_items) {
                if (item.inspection_photos && item.inspection_photos.length > 0) {
                  const photosWithUrls = await Promise.all(
                    item.inspection_photos.map(async (photo) => {
                      if (!photo.storage_path) {
                        return { ...photo, url: null };
                      }

                      const { url, error: urlError } = await getSignedPhotoUrl(photo.storage_path);

                      if (urlError) {
                        console.error(`[getAllInspections] Error getting signed URL for photo ${photo.id}:`, urlError);
                      }

                      return {
                        ...photo,
                        url: url || null,
                      };
                    })
                  );

                  item.inspection_photos = photosWithUrls;
                }
              }
            }
          }
        }
      }
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching inspections:', error);
    throw error;
  }
}

/**
 * Get a single inspection by ID with signed photo URLs
 * @param {string} id - Inspection ID
 * @returns {Promise<Object>} Inspection object with rooms, items, and photo URLs
 */
export async function getInspection(id) {
  try {
    const { data, error } = await supabase
      .from('inspections')
      .select(`
        *,
        leases (
          id,
          tenant_name,
          tenant_email,
          start_date,
          end_date,
          status
        ),
        inspection_rooms (
          *,
          inspection_items (
            *,
            inspection_photos (*)
          )
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    // Generate signed URLs for all photos
    if (data?.inspection_rooms) {
      for (const room of data.inspection_rooms) {
        if (room.inspection_items) {
          for (const item of room.inspection_items) {
            if (item.inspection_photos && item.inspection_photos.length > 0) {
              const photosWithUrls = await Promise.all(
                item.inspection_photos.map(async (photo) => {
                  if (!photo.storage_path) {
                    return { ...photo, url: null };
                  }

                  const { url, error: urlError } = await getSignedPhotoUrl(photo.storage_path);

                  if (urlError) {
                    console.error(`[getInspection] Error getting signed URL for photo ${photo.id}:`, urlError);
                  }

                  return {
                    ...photo,
                    url: url || null,
                  };
                })
              );

              item.inspection_photos = photosWithUrls;
            }
          }
        }
      }
    }

    return data;
  } catch (error) {
    console.error('Error fetching inspection:', error);
    throw error;
  }
}

/**
 * Create a new inspection with rooms and items
 * @param {Object} inspection - Inspection data
 * @returns {Promise<Object>} Created inspection
 */
export async function createInspection(inspection) {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check subscription limits for inspection type
    const profile = await getUserProfile(user.id);
    const permission = canCreateInspection(profile, inspection.type);

    if (!permission.allowed) {
      const error = new Error(permission.reason);
      error.requiresUpgrade = permission.requiresUpgrade;
      throw error;
    }

    // Check per-property inspection limit (for free users)
    const { count: currentInspectionCount } = await supabase
      .from('inspections')
      .select('*', { count: 'exact', head: true })
      .eq('property_id', inspection.propertyId);

    const propertyPermission = canCreateInspectionForProperty(profile, currentInspectionCount || 0);

    if (!propertyPermission.allowed) {
      const error = new Error(propertyPermission.reason);
      error.requiresUpgrade = propertyPermission.requiresUpgrade;
      throw error;
    }

    // Create inspection
    const { data: newInspection, error: inspectionError } = await supabase
      .from('inspections')
      .insert({
        property_id: inspection.propertyId,
        lease_id: inspection.leaseId || null,
        type: inspection.type,
        date: inspection.date,
        inspector: inspection.inspector || null,
        overall_notes: inspection.overallNotes || null,
        created_by_user_id: user.id, // Track who created this inspection (owner)
      })
      .select()
      .single();

    if (inspectionError) throw inspectionError;

    // Create rooms and items
    if (inspection.rooms && inspection.rooms.length > 0) {
      for (const room of inspection.rooms) {
        const { data: newRoom, error: roomError } = await supabase
          .from('inspection_rooms')
          .insert({
            inspection_id: newInspection.id,
            name: room.name,
            display_order: room.displayOrder || 0,
          })
          .select()
          .single();

        if (roomError) throw roomError;

        // Create items for this room (one by one to get IDs for photo upload)
        if (room.items && room.items.length > 0) {
          for (let index = 0; index < room.items.length; index++) {
            const item = room.items[index];

            // Create item
            const { data: newItem, error: itemError } = await supabase
              .from('inspection_items')
              .insert({
                room_id: newRoom.id,
                name: item.name,
                condition: item.condition,
                notes: item.notes || null,
                display_order: index,
              })
              .select()
              .single();

            if (itemError) throw itemError;

            // Upload photos for this item (if any)
            if (item.photos && item.photos.length > 0) {
              for (const photoData of item.photos) {
                // Skip if photo is already a URL (shouldn't happen, but safe check)
                if (typeof photoData === 'string' && photoData.startsWith('http')) {
                  continue;
                }

                // Convert base64 to File
                const base64String = typeof photoData === 'string' ? photoData : photoData.data;
                if (!base64String || !base64String.startsWith('data:')) {
                  console.warn('Invalid photo data format, skipping');
                  continue;
                }

                const file = base64ToFile(base64String, 'photo.jpg');

                // Upload to Supabase Storage
                const uploadResult = await uploadPhoto(file, {
                  userId: user.id,
                  inspectionId: newInspection.id,
                  itemId: newItem.id,
                });

                if (uploadResult.error) {
                  console.error('Photo upload failed:', uploadResult.error);
                  continue; // Continue with other photos even if one fails
                }

                // Save photo record to database
                const { error: photoError } = await supabase
                  .from('inspection_photos')
                  .insert({
                    item_id: newItem.id,
                    storage_path: uploadResult.path,
                    file_size: uploadResult.size,
                  });

                if (photoError) {
                  console.error('Photo record creation failed:', photoError);
                }
              }
            }
          }
        }
      }
    }

    // Fetch complete inspection with relations
    return await getInspection(newInspection.id);
  } catch (error) {
    console.error('Error creating inspection:', error);
    throw error;
  }
}

/**
 * Update an inspection
 * @param {string} id - Inspection ID
 * @param {Object} updates - Inspection updates
 * @returns {Promise<Object>} Updated inspection
 */
export async function updateInspection(id, updates) {
  try {
    const { data, error } = await supabase
      .from('inspections')
      .update({
        type: updates.type,
        date: updates.date,
        inspector: updates.inspector || null,
        overall_notes: updates.overallNotes || null,
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating inspection:', error);
    throw error;
  }
}

/**
 * Update an inspection item
 * @param {string} itemId - Item ID
 * @param {Object} updates - Item updates
 * @returns {Promise<Object>} Updated item
 */
export async function updateInspectionItem(itemId, updates) {
  try {
    const { data, error } = await supabase
      .from('inspection_items')
      .update({
        name: updates.name,
        condition: updates.condition,
        notes: updates.notes || null,
      })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating inspection item:', error);
    throw error;
  }
}

/**
 * Delete an inspection
 * @param {string} id - Inspection ID
 * @returns {Promise<void>}
 */
export async function deleteInspection(id) {
  try {
    const { error } = await supabase
      .from('inspections')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting inspection:', error);
    throw error;
  }
}

/**
 * Add a photo to an inspection item
 * @param {string} itemId - Item ID
 * @param {string} storagePath - Path to photo in storage
 * @param {number} fileSize - File size in bytes
 * @returns {Promise<Object>} Created photo record
 */
export async function addInspectionPhoto(itemId, storagePath, fileSize) {
  try {
    const { data, error } = await supabase
      .from('inspection_photos')
      .insert({
        item_id: itemId,
        storage_path: storagePath,
        file_size: fileSize,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding inspection photo:', error);
    throw error;
  }
}

/**
 * Delete an inspection photo
 * @param {string} photoId - Photo ID
 * @returns {Promise<void>}
 */
export async function deleteInspectionPhoto(photoId) {
  try {
    const { error } = await supabase
      .from('inspection_photos')
      .delete()
      .eq('id', photoId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting inspection photo:', error);
    throw error;
  }
}

/**
 * Create a periodic inspection as tenant
 * Similar to createInspection but with tenant-specific permissions
 * @param {Object} inspection - Inspection data (must be type='periodic')
 * @param {string} ownerId - Property owner's user ID (for storage path)
 * @returns {Promise<Object>} Created inspection
 */
export async function createTenantInspection(inspection, ownerId) {
  try {
    // Get current user (tenant)
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Validate tenant has permission for this property
    const permission = await canTenantAccessProperty(inspection.propertyId);
    if (!permission.allowed) {
      const error = new Error(permission.reason);
      error.requiresUpgrade = false;
      throw error;
    }

    // Force periodic type (tenants can only create periodic inspections)
    if (inspection.type !== 'periodic') {
      throw new Error('Najemcy mogą tworzyć tylko inspekcje okresowe');
    }

    // Create inspection (RLS will enforce type='periodic' at database level too)
    const { data: newInspection, error: inspectionError } = await supabase
      .from('inspections')
      .insert({
        property_id: inspection.propertyId,
        lease_id: inspection.leaseId || null,
        type: 'periodic',
        date: inspection.date,
        inspector: inspection.inspector || null,
        overall_notes: inspection.overallNotes || null,
        created_by_user_id: user.id, // Track who created this inspection (tenant)
      })
      .select()
      .single();

    if (inspectionError) throw inspectionError;

    // Create rooms and items (same as owner flow)
    if (inspection.rooms && inspection.rooms.length > 0) {
      for (const room of inspection.rooms) {
        const { data: newRoom, error: roomError } = await supabase
          .from('inspection_rooms')
          .insert({
            inspection_id: newInspection.id,
            name: room.name,
            display_order: room.displayOrder || 0,
          })
          .select()
          .single();

        if (roomError) throw roomError;

        // Create items for this room (one by one to get IDs for photo upload)
        if (room.items && room.items.length > 0) {
          for (let index = 0; index < room.items.length; index++) {
            const item = room.items[index];

            // Create item
            const { data: newItem, error: itemError } = await supabase
              .from('inspection_items')
              .insert({
                room_id: newRoom.id,
                name: item.name,
                condition: item.condition,
                notes: item.notes || null,
                display_order: index,
              })
              .select()
              .single();

            if (itemError) throw itemError;

            // Upload photos for this item (if any)
            // CRITICAL: Use ownerId (property owner) NOT tenant's user.id
            if (item.photos && item.photos.length > 0) {
              for (const photoData of item.photos) {
                // Skip if photo is already a URL
                if (typeof photoData === 'string' && photoData.startsWith('http')) {
                  continue;
                }

                // Convert base64 to File
                const base64String = typeof photoData === 'string' ? photoData : photoData.data;
                if (!base64String || !base64String.startsWith('data:')) {
                  console.warn('Invalid photo data format, skipping');
                  continue;
                }

                const file = base64ToFile(base64String, 'photo.jpg');

                // Upload to Supabase Storage using OWNER's ID
                const uploadResult = await uploadPhoto(file, {
                  userId: ownerId, // ← CRITICAL: Owner's ID, not tenant's
                  inspectionId: newInspection.id,
                  itemId: newItem.id,
                });

                if (uploadResult.error) {
                  console.error('Photo upload failed:', uploadResult.error);
                  continue; // Continue with other photos even if one fails
                }

                // Save photo record to database
                const { error: photoError } = await supabase
                  .from('inspection_photos')
                  .insert({
                    item_id: newItem.id,
                    storage_path: uploadResult.path,
                    file_size: uploadResult.size,
                  });

                if (photoError) {
                  console.error('Photo record creation failed:', photoError);
                }
              }
            }
          }
        }
      }
    }

    // Fetch complete inspection with relations
    return await getInspection(newInspection.id);
  } catch (error) {
    console.error('Error creating tenant inspection:', error);
    throw error;
  }
}
